/** Application render dispatcher and production shell composition. */
import '../shell-search.css';
import './index.css';

import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { PopupMenu } from '@kerfjs/ui/popup-menu';
import { Text } from '@kerfjs/ui/text';
import { wireWorkbench } from '@kerfjs/ui/wire-workbench';
import { Workbench } from '@kerfjs/ui/workbench';
import type { SafeHtml } from 'kerfjs';

import { CardHeader } from '../../components/card-header.js';
import { PageLoading } from '../../components/page-loading.js';
import {
  contextMenu,
  dialogUnsavedPrompt,
  editorSegment,
  inspectorCollapsed,
  libraryDocs,
  libraryScope,
  matrixSelection,
  me,
  selectedDocumentIds,
  selectedLibraryDocIds,
  selectedLineIds,
  settingsPage,
  settingsTab,
  sidebarCollapsed,
  status,
  unsavedChangesPrompt,
  vendorDetail,
  vendorsOverview,
  view,
  workspace,
} from '../../data/state.js';
import { menuProps, type MenuTrigger } from '../../lib/action-menu.js';
import { demandReadOnly, isOrgAdmin } from '../../lib/context.js';
import { editorTransferTarget } from '../../lib/editor-transfer-target.js';
import { SETTINGS_AREAS, settingsLocation } from '../../lib/settings-nav.js';
import { assistantPanel } from '../chat.js';
import { DocumentActionsMenu } from '../demand-editor/components/document-actions-menu.js';
import { matrixCellMenuEntries } from '../demand-editor/components/price-matrix.js';
import { SelectionActionsMenu } from '../demand-editor/components/selection-actions-menu.js';
import { DemandEditorBanners, DemandEditorSubHeader, Onboarding } from '../demand-editor/index.js';
import {
  LibraryDocumentActionsMenu,
  selectedLibraryDocuments,
} from '../library/components/library-document-actions-menu.js';
import { LibraryHeader } from '../library/components/library-header.js';
import { CurrenciesHeader } from '../settings/currencies-header.js';
import { MembersHeader } from '../settings/members-header.js';
import { SettingsFooter, settingsFooterAction } from '../settings/settings-footer.js';
import { SitesHeader } from '../settings/sites-header.js';
import { UnsavedChangesDialog } from '../settings/unsaved-changes-dialog.js';
import { VendorsHeader } from '../vendors/vendors-header.js';
import { ActingAsBanner } from './components/acting-as-banner.js';
import { chatRowMenuEntries } from './components/chat-sidebar-row.js';
import { DemandFooter, demandFooterShown } from './components/demand-footer.js';
import { demandRowMenuEntries } from './components/demand-sidebar-row.js';
import { DetailView } from './components/detail-view.js';
import { mainToolbarConfig } from './components/main-toolbar.js';
import { OrganizationChoice } from './components/organization-choice.js';
import { ProjectTabsHeader } from './components/project-tabs-header.js';
import { sidebarPanel } from './components/sidebar.js';

export function render(): SafeHtml {
  switch (status.value) {
    case 'loading':
      return (
        <main class="app-entry">
          <PageLoading label="Loading" />
        </main>
      );
    case 'anon':
      return (
        <main class="app-entry">
          <div class="entry-card">
            <ContentItem appearance="surface" frame="framed">
              <CardHeader title="Karwan Procurement" size="xlarge" headingLevel={1} />
              <ListInsetControl>
                <Text tone="quiet">AI procurement decision-support.</Text>
              </ListInsetControl>
              <ListInsetControl>
                <Button size="default" tone="brand" emphasis="primary" href="/signin">
                  Sign In
                </Button>
              </ListInsetControl>
            </ContentItem>
          </div>
        </main>
      );
    case 'onboarding':
      return (
        <main class="app-entry">
          <Onboarding />
        </main>
      );
    case 'choosing-organization':
      return <OrganizationChoice />;
    case 'suspended':
      return (
        <main class="app-entry">
          <div class="entry-card">
            <ContentItem appearance="surface" frame="framed">
              <CardHeader title="Your account is suspended." size="xlarge" headingLevel={1} />

              <ListInsetControl>
                <Text tone="danger">
                  Your access to {me.value?.email ?? 'this organization'} has been paused. An administrator can restore
                  it. If you think this is a mistake, please contact them.
                </Text>
              </ListInsetControl>

              <ListInsetControl>
                <Button tone="neutral" emphasis="primary" size="default" action="signout">
                  Sign Out
                </Button>
              </ListInsetControl>
            </ContentItem>
          </div>
        </main>
      );
    case 'ready':
      return <AppShell />;
  }
}

/** Put the acting-as strip (HS2-RHZ75A) above whatever header the view has. */
function withActingAsBanner(header: SafeHtml | undefined): SafeHtml | undefined {
  const viewer = me.value;
  if (viewer?.actingAs === null || viewer?.actingAs === undefined) return header;
  return (
    <>
      <ActingAsBanner viewer={viewer} />
      {header}
    </>
  );
}

/**
 * Role-aware three-column shell on Kerf's Workbench. The rails' toolbars and
 * show/hide toggles are Workbench-managed: a collapsed rail's switcher and
 * toggle move into the work-area toolbar (HS2-AC9K4G). The open demand's
 * sub-header and banners are the fixed `mainHeader`; draft submission and a requester's
 * Withdraw to Draft use `mainFooter` (HS2-X22G63).
 */
export function AppShell(): SafeHtml {
  const currentView = view.value;
  const transferTarget =
    (currentView === 'workspace' || currentView === 'intake') && workspace.value !== null
      ? editorTransferTarget(editorSegment.value, demandReadOnly())
      : null;
  const demandOpen = (currentView === 'workspace' || currentView === 'intake') && workspace.value !== null;
  const libraryDrop = currentView === 'library';
  // Project settings pages choose their project in a fixed tab bar (HS2-91S2YF).
  const settingsPosition = settingsLocation(currentView, {
    area: settingsTab.value,
    page: settingsPage.value,
    libraryScope: libraryScope.value,
  });
  const projectSettings = settingsPosition?.area === 'project';
  const settingsHasSave =
    currentView === 'settings' &&
    settingsPosition !== null &&
    settingsFooterAction(settingsPosition.area, settingsPosition.page) !== null;
  return (
    <>
      <Workbench
        id="procurement-workbench"
        label="Karwan Procurement"
        className="app-shell"
        mainToolbar={mainToolbarConfig({ view: currentView })}
        mainHeader={withActingAsBanner(
          demandOpen ? (
            <>
              <DemandEditorSubHeader />
              <DemandEditorBanners />
            </>
          ) : currentView === 'library' ? (
            <>
              {projectSettings ? <ProjectTabsHeader /> : null}
              <LibraryHeader />
            </>
          ) : currentView === 'settings' && settingsPosition?.page === 'currencies' ? (
            <CurrenciesHeader />
          ) : currentView === 'settings' && settingsPosition?.page === 'sites' && isOrgAdmin() ? (
            <SitesHeader />
          ) : currentView === 'settings' && settingsPosition?.page === 'members' && isOrgAdmin() ? (
            <MembersHeader />
          ) : currentView === 'vendors' && vendorsOverview.value !== null ? (
            <VendorsHeader vendorId={vendorDetail.value?.vendor.id ?? null} />
          ) : projectSettings ? (
            <ProjectTabsHeader />
          ) : undefined,
        )}
        mainFooter={
          demandOpen && demandFooterShown() ? <DemandFooter /> : settingsHasSave ? <SettingsFooter /> : undefined
        }
        mainLabel={transferTarget?.label}
        mainTabIndex={transferTarget === null ? undefined : 0}
        mainOutlined={transferTarget !== null}
        mainPane={{
          contentElement: 'main',
          contentLabel: 'Procurement Workspace',
          chromeDividers: 'always',
          appearance: 'sunken',
          deepInset: true,
        }}
        main={
          <>
            <DetailView view={currentView} />
            {transferTarget === null && !libraryDrop ? null : (
              <div class="editor-drop-overlay" aria-hidden="true">
                <span class="editor-drop-overlay-text">
                  {libraryDrop ? 'Drop documents into the Knowledge Base' : transferTarget?.hint}
                </span>
              </div>
            )}
          </>
        }
        leftRail={sidebarPanel({ view: currentView, collapsed: sidebarCollapsed.value })}
        rightRail={assistantPanel({ view: currentView, collapsed: inspectorCollapsed.value })}
      />
      {contextMenuHost()}
      {unsavedChangesHost()}
    </>
  );
}

/** The unsaved-changes question while a navigation away from an explicit-save page is held (HS2-HV4R7C). */
function unsavedChangesHost(): SafeHtml {
  // A dialog's held close (HS2-FSBQVX) is asked about first; it sits above the dialog.
  const held = dialogUnsavedPrompt.value;
  if (held !== null) return <UnsavedChangesDialog pageLabel={held.subject} closing />;
  const prompt = unsavedChangesPrompt.value;
  if (prompt === null) return <></>;
  const label = SETTINGS_AREAS.organization.pages.find((page) => page.id === prompt.page)?.label ?? 'this page';
  return <UnsavedChangesDialog pageLabel={label} saving={prompt.saving} />;
}

/** The one open right-click menu, rendered only while it is open and taken out of flow so it never
 *  adds height to the list it was opened from (HS2-3KAZPW). */
function contextMenuHost(): SafeHtml {
  const open = contextMenu.value;
  if (open === null) return <></>;
  const { target } = open;
  const trigger: MenuTrigger = {
    label: 'Actions',
    context: true,
    rootAttributes: { 'data-context-menu': target.kind, 'data-context-menu-id': String(open.id) },
  };
  return (
    <div class="context-menu-host" data-key={String(open.id)}>
      {target.kind === 'demand-row' ? (
        <PopupMenu {...menuProps(trigger, demandRowMenuEntries(target.demandId, target.actions))} />
      ) : target.kind === 'chat-row' ? (
        <PopupMenu {...menuProps(trigger, chatRowMenuEntries(target.chatId, target.closed))} />
      ) : target.kind === 'library-documents' ? (
        <LibraryDocumentActionsMenu
          selected={selectedLibraryDocuments(libraryDocs.value, selectedLibraryDocIds.value)}
          trigger={trigger}
        />
      ) : target.kind === 'matrix-cells' ? (
        <PopupMenu {...menuProps(trigger, matrixCellMenuEntries(matrixSelection.value.selected.length))} />
      ) : editorSegment.value === 'files' ? (
        <DocumentActionsMenu count={selectedDocumentIds.value.length} trigger={trigger} />
      ) : (
        <SelectionActionsMenu count={selectedLineIds.value.length} trigger={trigger} />
      )}
    </div>
  );
}

/**
 * Wire the app shell's Workbench once for the page-lifetime root. Below Kerf's
 * `narrow` breakpoint the rails overlay the work area; with their `collapsed`
 * signals wired, an overlay rail starts collapsed, opens one at a time, takes
 * focus and traps Tab, and closes on Escape or an outside press, and the rails
 * get their inline state back above the breakpoint (HS2-P4FKCY).
 */
export function wireAppShell(root: HTMLElement): () => void {
  return wireWorkbench(root, {
    id: 'procurement-workbench',
    panels: {
      leftRail: { collapsed: sidebarCollapsed },
      rightRail: { collapsed: inspectorCollapsed },
    },
  });
}
